import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useRequireAuth } from "../../../utils/requireAuthRedirect";
import { resolveTickerBackNavigation, goBackFromTicker } from "../../../utils/stockDashboardNav";
import { fetchTimingDetail, fetchMarketStructureDetail } from "../services/detailApi";
import MatriceDecisionPage from "../detail/MatriceDecision";
import AnalyseFondamentaleCard from "../detail/AnalyseFondamentaleCard";
import TimingMarcheCard from "../detail/TimingMarcheCard";
import MarketStructureCard from "../detail/MarketStructureCard";

function useSymbolBack() {
  const navigate = useNavigate();
  const location = useLocation();
  const { symbol } = useParams();
  const back = resolveTickerBackNavigation(location, "fr");
  const onBack = useCallback(() => {
    if (symbol) {
      navigate(`/marketflow/ticker/${encodeURIComponent(symbol)}`, { state: location.state });
    } else {
      goBackFromTicker(navigate, back, location.state);
    }
  }, [navigate, symbol, back, location.state]);
  return { symbol: decodeURIComponent(symbol || ""), onBack };
}

export function DecisionDetailPage() {
  const isLoggedIn = useRequireAuth();
  const { symbol, onBack } = useSymbolBack();
  if (!isLoggedIn) return null;
  return <MatriceDecisionPage symbol={symbol} onBack={onBack} />;
}

export function FundamentalsDetailPage() {
  const isLoggedIn = useRequireAuth();
  const { symbol, onBack } = useSymbolBack();
  if (!isLoggedIn) return null;
  return <AnalyseFondamentaleCard key={symbol} symbol={symbol} onBack={onBack} />;
}

export function TimingDetailPage() {
  const isLoggedIn = useRequireAuth();
  const { symbol, onBack } = useSymbolBack();
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isLoggedIn || !symbol) return;
    const ctrl = new AbortController();
    setLoading(true);
    fetchTimingDetail(symbol, { signal: ctrl.signal })
      .then(setPayload)
      .catch((e) => { if (e.name !== "AbortError") setError(e.message); })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [isLoggedIn, symbol]);

  if (!isLoggedIn) return null;
  if (loading) return <div style={{ padding: 48, textAlign: "center", color: "var(--muted)" }}>Chargement du timing de marché…</div>;
  if (error) return <div style={{ padding: 48, textAlign: "center", color: "#c6523f" }}>{error}</div>;

  const d = { ...(payload?.d || {}), priceChart: payload?.priceChart, rsChart: payload?.rsChart, statsChart: payload?.statsChart };
  return (
    <div>
      <div style={{ padding: "12px 20px 0", maxWidth: 1820, margin: "0 auto" }}>
        <button type="button" onClick={onBack} style={{
          background: "none", border: "none", color: "var(--muted)", fontSize: 12, fontWeight: 700,
          marginBottom: 10, cursor: "pointer",
        }}>← Retour au Dashboard</button>
      </div>
      <TimingMarcheCard d={d} nav={navigate} />
    </div>
  );
}

export function MarketStructureDetailPage() {
  const isLoggedIn = useRequireAuth();
  const { symbol, onBack } = useSymbolBack();
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(null);

  useEffect(() => {
    if (!isLoggedIn || !symbol) return;
    const ctrl = new AbortController();
    setLoading(true);
    fetchMarketStructureDetail(symbol, year, { signal: ctrl.signal })
      .then((data) => {
        setPayload(data);
        if (!year && data?.calendarYear) setYear(data.calendarYear);
      })
      .catch((e) => { if (e.name !== "AbortError") setError(e.message); })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [isLoggedIn, symbol, year]);

  if (!isLoggedIn) return null;
  if (loading && !payload) return <div style={{ padding: 48, textAlign: "center", color: "var(--muted)" }}>Chargement de la structure de marché…</div>;
  if (error && !payload) return <div style={{ padding: 48, textAlign: "center", color: "#c6523f" }}>{error}</div>;
  return (
    <MarketStructureCard
      externalD={payload?.d}
      symbol={symbol}
      onBack={onBack}
      availableYears={payload?.availableYears}
      selectedYear={year || payload?.calendarYear}
      onYearChange={setYear}
      apiMode
    />
  );
}
